import React from 'react';
import { Box, Paper, ToggleButtonGroup, ToggleButton, IconButton, Tooltip, Typography, Button, Checkbox, FormControlLabel } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { ConditionBuilder } from './ConditionBuilder';
import type { LogisticsObjectScope } from '@/types/logisticsObject';
import { QueryExpression } from '@/api/bol';

export interface ConditionGroupProps {
  query: QueryExpression;
  onChange: (query: QueryExpression) => void;
  onRemove?: () => void;
  disabled?: boolean;
  depth?: number;
  maxDepth?: number;
  showRemoveButton?: boolean;
  logisticsObjectType?: LogisticsObjectScope;
}

export const ConditionGroup: React.FC<ConditionGroupProps> = ({
  query,
  onChange,
  onRemove,
  disabled = false,
  depth = 0,
  maxDepth = 3,
  showRemoveButton = true,
  logisticsObjectType = 'all'
}) => {
  const isNegated = query.not !== undefined;
  const displayQuery = isNegated ? (query.not || {}) : query;
  
  let logicalOperator: 'and' | 'or' | undefined = undefined;
  if (displayQuery.and) {
    logicalOperator = 'and';
  } else if (displayQuery.or) {
    logicalOperator = 'or';
  }
  const children = displayQuery.and || displayQuery.or || [];
  const isGroup = logicalOperator !== undefined;

  // Helper to wrap/unwrap query with NOT operator
  const wrapQuery = (newQuery: QueryExpression): QueryExpression => {
    return isNegated ? { not: newQuery } : newQuery;
  };

  const handleLogicalOperatorChange = (
    _event: React.MouseEvent<HTMLElement>,
    newOperator: 'and' | 'or' | null
  ) => {
    if (!newOperator || !isGroup) return;
    onChange(wrapQuery({ [newOperator]: children }));
  };

  const handleNegateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked ? { not: displayQuery } : displayQuery);
  };

  const handleChildChange = (index: number, newChild: QueryExpression) => {
    const newChildren = [...children];
    newChildren[index] = newChild;
    onChange(wrapQuery({ [logicalOperator!]: newChildren }));
  };

  const handleAddCondition = () => {
    if (!isGroup || !logicalOperator) return;
    onChange(wrapQuery({ [logicalOperator]: [...children, {}] }));
  };

  const handleAddGroup = () => {
    if (!isGroup || depth >= maxDepth || !logicalOperator) return;
    onChange(wrapQuery({ [logicalOperator]: [...children, { and: [{}] }] }));
  };

  const handleRemoveChild = (index: number) => {
    if (!isGroup || !logicalOperator) return;
    
    const newChildren = children.filter((_, i) => i !== index);
    
    if (newChildren.length === 1 && depth > 0) {
      onChange(wrapQuery(newChildren[0]));
    } else if (newChildren.length === 0) {
      onChange(wrapQuery({}));
    } else {
      onChange(wrapQuery({ [logicalOperator]: newChildren }));
    }
  };

  const handleCollapseGroup = () => {
    onChange(wrapQuery(children.length === 1 ? children[0] : {}));
  };

  // Single condition (not a group)
  if (!isGroup) {
    return (
      <ConditionBuilder
        query={query}
        onChange={onChange}
        onRemove={onRemove}
        disabled={disabled}
        showRemoveButton={showRemoveButton}
        logisticsObjectType={logisticsObjectType}
      />
    );
  }

  // Group (AND/OR)
  const getBorderColor = () => {
    if (isNegated) return 'error.main';
    return logicalOperator === 'and' ? 'primary.main' : 'secondary.main';
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        backgroundColor: depth % 2 === 0 ? 'background.default' : 'background.paper',
        borderLeftWidth: 3,
        borderLeftColor: getBorderColor(),
        ...(isNegated && {
          backgroundColor: 'error.50'
        })
      }}
    >
      {/* Group Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          <Typography 
            variant="subtitle2" 
            color="text.secondary" 
            sx={{ 
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 1,
              minWidth: 0
            }}
          >
            Combine conditions with:
          </Typography>
          <Box sx={{ flexShrink: 0 }}>
            <ToggleButtonGroup
              value={logicalOperator}
              exclusive
              onChange={handleLogicalOperatorChange}
              size="small"
              disabled={disabled}
            >
              <Tooltip title="All conditions below must match">
                <ToggleButton value="and">
                  <Typography variant="caption" fontWeight="bold">AND</Typography>
                </ToggleButton>
              </Tooltip>
              <Tooltip title="At least one condition below must match">
                <ToggleButton value="or">
                  <Typography variant="caption" fontWeight="bold">OR</Typography>
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={isNegated}
                onChange={handleNegateChange}
                disabled={disabled}
                size="small"
                color="error"
              />
            }
            label="NOT"
            sx={{ ml: 1, flexShrink: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem', fontWeight: isNegated ? 600 : 400 } }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 1 }}>
          {depth === 0 && (
            <Tooltip title={children.length === 1 ? "Collapse to single condition" : "Clear all conditions"}>
              <IconButton
                onClick={handleCollapseGroup}
                size="small"
                color="error"
                disabled={disabled}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {showRemoveButton && onRemove && depth > 0 && (
            <Tooltip title="Remove group">
              <IconButton
                onClick={onRemove}
                size="small"
                color="error"
                disabled={disabled}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Children */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {children.map((child, index) => {
          const childIsNegated = child.not !== undefined;
          const childBase = childIsNegated ? (child.not || {}) : child;
          const childIsGroup = childBase.and || childBase.or;
          
          return (
            <Box key={`${depth}-${index}`} sx={{ position: 'relative' }}>
              {childIsGroup ? (
                <ConditionGroup
                  query={child}
                  onChange={(newChild) => handleChildChange(index, newChild)}
                  onRemove={() => handleRemoveChild(index)}
                  disabled={disabled}
                  depth={depth + 1}
                  maxDepth={maxDepth}
                  showRemoveButton={true}
                  logisticsObjectType={logisticsObjectType}
                />
              ) : (
                <ConditionBuilder
                  query={child}
                  onChange={(newChild) => handleChildChange(index, newChild)}
                  onRemove={() => handleRemoveChild(index)}
                  disabled={disabled}
                  showRemoveButton={true}
                  logisticsObjectType={logisticsObjectType}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Add Actions */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <Button
          startIcon={<Add />}
          onClick={handleAddCondition}
          disabled={disabled}
          size="small"
          variant="outlined"
        >
          Add Condition
        </Button>
        
        {depth < maxDepth && (
          <Button
            startIcon={<Add />}
            onClick={handleAddGroup}
            disabled={disabled}
            size="small"
            variant="outlined"
          >
            Add Group
          </Button>
        )}
      </Box>
    </Paper>
  );
};
